import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearch, useRouter } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  resetPasswordSchema,
  type ResetPasswordForm,
} from '@/lib/schemas/profile';
import { useConfirmPasswordReset } from '@/lib/api/profile';
import { ApiError } from '@/lib/api';

/**
 * /auth/reset-password?token=... (AK-230) — установить новый пароль.
 *
 * Token приходит из письма (см. backend `password_reset_request`).
 * Backend `POST /auth/password-reset/confirm` принимает { token, new_password }
 * и **отзывает все активные сессии** — так что после успеха редиректим на
 * /login (юзер залогинится заново с новым паролем).
 *
 * Если token отсутствует в URL — показываем ошибку и ссылку на forgot-password.
 */
export function ResetPasswordPage() {
  const router = useRouter();
  const search = useSearch({ from: '/auth/reset-password' }) as {
    token?: string;
  };
  const token = search.token;
  const confirmMut = useConfirmPasswordReset();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    // AK-465 (добивка к PR #534): НЕ возвращать 'onBlur'. В mode:'onBlur' RHF
    // пропускает change-события — `isValid` не поднимается до blur'а, а кнопка
    // ниже = `disabled={isSubmitting || !isValid}` → залипает disabled, и Enter
    // в поле «Повторите новый пароль» не сабмитит (implicit submission не
    // работает через disabled default button). Форма — единственный путь
    // восстановления доступа по ссылке из письма, залипание = потеря аккаунта
    // до повторного reset-запроса. 'onChange' валидирует на каждом keystroke.
    mode: 'onChange',
    defaultValues: { new_password: '', confirm_password: '' },
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return;
    setServerError(null);
    try {
      await confirmMut.mutateAsync({
        token,
        new_password: data.new_password,
      });
      setSuccess(true);
      window.setTimeout(() => {
        router.navigate({ to: '/login' });
      }, 1500);
    } catch (err) {
      if (err instanceof ApiError) setServerError(err.detail);
      else setServerError('Сетевая ошибка');
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold">Новый пароль</CardTitle>
          <CardDescription>
            Придумайте новый пароль для входа в систему
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Ссылка некорректна. Возможно, она устарела.
                </AlertDescription>
              </Alert>
              <Button asChild variant="outline" className="w-full">
                <a href="/auth/forgot-password">Запросить новую ссылку</a>
              </Button>
            </div>
          ) : success ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Пароль обновлён. Перенаправляем на страницу входа…
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {serverError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{serverError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="reset-new-password">Новый пароль</Label>
                <PasswordInput
                  id="reset-new-password"
                  autoComplete="new-password"
                  aria-invalid={!!errors.new_password}
                  className="h-10"
                  {...register('new_password')}
                />
                {errors.new_password && (
                  <p className="text-destructive text-xs mt-1">
                    {errors.new_password.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reset-confirm-password">
                  Повторите новый пароль
                </Label>
                <PasswordInput
                  id="reset-confirm-password"
                  autoComplete="new-password"
                  aria-invalid={!!errors.confirm_password}
                  className="h-10"
                  {...register('confirm_password')}
                />
                {errors.confirm_password && (
                  <p className="text-destructive text-xs mt-1">
                    {errors.confirm_password.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !isValid}
                className="w-full"
                size="lg"
              >
                {isSubmitting ? 'Сохраняем…' : 'Установить новый пароль'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ResetPasswordPage;
